import { ExternalLink, Eye, Handshake } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useParceirosControllerListar } from '@/api/generated/parceiros/parceiros';
import type {
  ParceiroDaVitrineResponse,
  ParceirosControllerListarCategoria,
} from '@/api/generated/model';
import { useTrackingParceiro } from '@/features/parceiros/hooks/useTrackingParceiro';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { cn } from '@/shared/lib/cn';
import { rotaDoParceiro } from '@/shared/lib/rotas';
import { Badge } from '@/shared/ui/badge';
import { Skeleton } from '@/shared/ui/skeleton';

const TODAS = 'Todas';

/**
 * Vitrine de parceiros (F4-4).
 *
 * ⚠️ As categorias do filtro vêm do SERVIDOR, junto da lista. A V1 as derivava
 * dos parceiros já carregados (`Parceiros.tsx:36`), então escolher um filtro
 * apagava as outras opções da tela.
 */
export function ParceirosPage() {
  const [categoria, setCategoria] = useState<string>(TODAS);
  const { refDeImpressao, registrarClique } = useTrackingParceiro();

  const { data, isPending, isError, refetch } = useParceirosControllerListar(
    categoria === TODAS ? undefined : { categoria: categoria as ParceirosControllerListarCategoria },
  );

  if (isError) return <EstadoErro aoTentarDeNovo={() => void refetch()} />;

  const categorias = [TODAS, ...(data?.categorias ?? [])];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <Handshake className="w-7 h-7 text-brand" />
          Parceiros
        </h2>
        <p className="text-muted-foreground mt-1">
          Conteúdos, soluções e produtos de parceiros que ampliam as possibilidades de diagnóstico
          e tratamento, apoiando suas decisões clínicas.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {categorias.map((opcao) => (
          <button
            key={opcao}
            type="button"
            onClick={() => setCategoria(opcao)}
            aria-pressed={categoria === opcao}
            className={cn(
              'h-8 px-3 rounded-lg text-xs font-medium border transition-colors',
              categoria === opcao
                ? 'bg-brand text-brand-foreground border-brand'
                : 'bg-transparent text-foreground border-border hover:bg-muted',
            )}
          >
            {opcao}
          </button>
        ))}
      </div>

      {isPending ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-72 w-full rounded-xl" />
          ))}
        </div>
      ) : data.itens.length === 0 ? (
        <EstadoVazio
          icone={Handshake}
          titulo="Nenhum parceiro por aqui"
          descricao={
            categoria === TODAS
              ? 'Ainda não há parceiros publicados na plataforma.'
              : `Não há parceiros na categoria "${categoria}".`
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.itens.map((parceiro) => (
            <CardDoParceiro
              key={parceiro.id}
              parceiro={parceiro}
              refDeImpressao={refDeImpressao}
              aoClicarNoSite={registrarClique}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CardDoParceiro({
  parceiro,
  refDeImpressao,
  aoClicarNoSite,
}: {
  parceiro: ParceiroDaVitrineResponse;
  refDeImpressao: (id: string) => (elemento: HTMLElement | null) => void;
  aoClicarNoSite: (id: string) => void;
}) {
  return (
    <article
      ref={refDeImpressao(parceiro.id)}
      className="bg-surface-raised rounded-xl overflow-hidden border border-border hover:shadow-lg transition-shadow flex flex-col"
    >
      {/* `bg-surface-raised`, igual ao card: quando o logo upado não
          preenche o quadro 3:1 exato, a sobra se confunde com o fundo do
          card em vez de aparecer como uma borda branca. */}
      <div className="aspect-[3/1] bg-surface-raised flex items-center justify-center border-b border-border">
        {parceiro.logoUrl ? (
          // Sem padding e sempre o logo (nunca a capa): o quadro e o logo
          // compartilham a proporção 3:1 exigida do logo pelo servidor, então
          // ele preenche exato. A capa tem outra proporção e deixava cada card
          // com um enquadramento diferente.
          <img
            src={parceiro.logoUrl}
            alt={parceiro.nome}
            className="w-full h-full object-contain"
          />
        ) : (
          <Handshake className="w-12 h-12 text-muted-foreground/40" />
        )}
      </div>

      <div className="p-4 flex flex-col flex-1">
        <h3 className="text-lg font-bold text-foreground">{parceiro.nome}</h3>
        {parceiro.categoria && (
          <Badge className="bg-brand text-brand-foreground hover:bg-brand/80 text-xs mt-1 w-fit">
            {parceiro.categoria}
          </Badge>
        )}

        <p className="text-sm text-muted-foreground line-clamp-3 mt-3 flex-1">
          {parceiro.descricao ?? 'Parceiro do ecossistema Hormonalle.'}
        </p>

        <div className="flex gap-2 mt-4">
          <Link
            to={rotaDoParceiro(parceiro.id)}
            className="flex-1 flex items-center justify-center gap-2 h-9 text-sm font-medium text-brand bg-brand/10 hover:bg-brand/15 rounded-lg transition-colors"
          >
            <Eye className="w-4 h-4" />
            Ver mais
          </Link>

          {parceiro.link && (
            <a
              href={parceiro.link}
              target="_blank"
              // `noopener` não é estilo: sem ele a página do parceiro recebe
              // `window.opener` e pode navegar esta aba para onde quiser.
              rel="noopener noreferrer"
              onClick={() => aoClicarNoSite(parceiro.id)}
              className="flex-1 flex items-center justify-center gap-2 h-9 text-sm font-medium text-brand-foreground bg-brand hover:bg-brand/80 rounded-lg transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Site
            </a>
          )}
        </div>
      </div>
    </article>
  );
}
